// @section 1 Opportunity detail — the per-deal screen behind a pipeline card.
// Opens from /pipeline (AccountsV5) on card click. Shows the company + primary contact,
// the objective + heat, the current engine recommendation with Do/Skip (writes the
// did/skipped verdict back, the learning signal), and the full chronological activity
// timeline (F5). v5/CSS-var idiom, mirrors AcquisitionDetail.

import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import { getOpportunity, postRecommendationVerdict, type OpportunityDetail as Opp } from '../api';

// @subsection 1.1 Formatting helpers
function fmtDate(s: string | null): string {
  if (!s) return '—';
  return new Date(s).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}
function fmtUsd(cents: number | null): string {
  if (cents == null) return '—';
  const v = cents / 100;
  return v >= 1000 ? `$${Math.round(v / 1000)}K` : `$${v.toFixed(0)}`;
}
const DIR: Record<string, { txt: string; color: string }> = {
  inbound: { txt: 'IN', color: 'var(--brand-3)' },
  outbound: { txt: 'OUT', color: 'var(--tx-3)' },
  system: { txt: 'SYS', color: 'var(--tx-5)' },
};

// @section 2 Screen
export default function OpportunityDetail() {
  const { dealId } = useParams<{ dealId: string }>();
  const [data, setData] = useState<Opp | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!dealId) return;
    let alive = true;
    setData(null);
    setError(null);
    getOpportunity(dealId)
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setError(String(e)); });
    return () => { alive = false; };
  }, [dealId]);

  async function verdict(status: 'done' | 'skipped') {
    if (!data?.recommendation || !dealId) return;
    setBusy(true);
    try {
      await postRecommendationVerdict(data.recommendation.id, status);
      setData(await getOpportunity(dealId)); // refetch to reflect the closed recommendation
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  }

  if (error) return <Shell><div style={{ color: 'var(--neg)', marginTop: 16 }}>{error}</div></Shell>;
  if (!data) return <Shell><div style={{ color: 'var(--tx-3)', marginTop: 16 }}>Loading…</div></Shell>;

  const o = data.opportunity;
  return (
    <Shell>
      {/* @subsection 2.1 Header */}
      <div className="page-head" style={{ paddingBottom: 16 }}>
        <div className="page-title" style={{ marginBottom: 6 }}>
          <h1 style={{ marginRight: 8 }}>{data.company?.name ?? o.name}</h1>
          <span className="sub">{o.name}</span>
          <div className="toolbar" style={{ marginLeft: 'auto' }}>
            {o.stage && <span className="proto-stage-pill">{o.stage}{o.bd_stage ? ` · ${o.bd_stage}` : ''}</span>}
            {o.heat_score != null && (
              <span className="proto-stage-pill" style={{ background: 'var(--t2-bg)', color: 'var(--tx-2)' }}>heat {Number(o.heat_score).toFixed(2)}</span>
            )}
            {o.value_cents != null && (
              <span className="proto-stage-pill" style={{ background: 'var(--t2-bg)', color: 'var(--tx-2)' }}>{fmtUsd(o.value_cents)}</span>
            )}
          </div>
        </div>
        <div style={{ fontSize: 12, color: 'var(--tx-3)' }}>
          {data.primary_contact?.name ?? 'no primary contact'}
          {data.primary_contact?.email ? ` · ${data.primary_contact.email}` : ''}
        </div>
        {o.objective && (
          <div style={{ marginTop: 8, fontSize: 12, color: 'var(--tx-2)' }}>
            <span style={{ color: 'var(--tx-4)', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, fontSize: 9, marginRight: 6 }}>Objective</span>
            {o.objective}
          </div>
        )}
      </div>

      <div className="proto-detail-body">
        <div className="proto-detail-grid">
          {/* @subsection 2.2 Recommended next action + Do/Skip */}
          <section className="proto-module span-2">
            <header><h3>Recommended next action</h3><span className="mod-tag">engine</span></header>
            <div className="proto-module-body">
              {data.recommendation ? (
                <>
                  <div style={{ fontSize: 13, color: 'var(--tx-0)', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                    {data.recommendation.recommended_action}
                  </div>
                  <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                    <button className="btn primary" disabled={busy} onClick={() => verdict('done')}>Did it</button>
                    <button className="btn" disabled={busy} onClick={() => verdict('skipped')}>Skip</button>
                  </div>
                </>
              ) : (
                <div style={{ fontSize: 12, color: 'var(--tx-4)', fontStyle: 'italic' }}>No open recommendation.</div>
              )}
            </div>
          </section>

          {/* @subsection 2.3 Activity timeline */}
          <section className="proto-module span-2">
            <header><h3>Timeline · {data.timeline.length}</h3><span className="mod-tag">activities</span></header>
            <div className="proto-module-body">
              {data.timeline.length === 0 ? (
                <div style={{ fontSize: 12, color: 'var(--tx-4)', fontStyle: 'italic' }}>No activity yet.</div>
              ) : (
                <ul className="proto-list">
                  {data.timeline.map((a) => {
                    const dir = a.direction ? DIR[a.direction] : null;
                    return (
                      <li key={a.id} className="proto-list-row" style={{ alignItems: 'flex-start' }}>
                        <div style={{ width: 92, flexShrink: 0, color: 'var(--tx-4)', fontSize: 10, marginRight: 12 }}>
                          <div>{fmtDate(a.occurred_at)}</div>
                          {dir && <div style={{ color: dir.color, fontWeight: 700, marginTop: 1 }}>{dir.txt}</div>}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ color: 'var(--tx-1)' }}>
                            {a.counterparty_name && <b style={{ marginRight: 6 }}>{a.counterparty_name}</b>}
                            <span>{a.subject ?? a.activity_type}</span>
                          </div>
                          {a.body && (
                            <div style={{ fontSize: 11, color: 'var(--tx-3)', marginTop: 2, lineHeight: 1.45 }}>
                              {a.body.length > 160 ? a.body.slice(0, 160) + '…' : a.body}
                            </div>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </section>
        </div>
      </div>
    </Shell>
  );
}

// @section 3 Shell — page chrome + back crumb to the pipeline
function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="v5-page">
      <div className="crumb">
        <NavLink to="/pipeline" style={{ color: 'var(--brand-3)' }}>← Pipeline</NavLink>
      </div>
      {children}
    </div>
  );
}
